import React, { useCallback } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { FormattedMessage, useIntl } from "react-intl";

import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import { getActivityTypeDeletingIds } from "@app/store/activity";
import { deleteActivityType } from "@app/store/activity/actions";
import { doesOrganizationSupportEmailService } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import styles from "../ActivityTypesCommon.module.scss";

interface Props {
  activityType: ActivityType;
  onActivate: (id: ActivityType["id"], active: boolean) => void;
  organizationSupportEmailService: boolean;
}

const DeleteButton: React.FC<Props> = ({
  activityType,
  onActivate,
  organizationSupportEmailService,
}) => {
  const intl = useIntl();

  const handleActivate = useCallback(
    () => onActivate(activityType.id, !activityType.active),
    [activityType, onActivate]
  );

  if (activityType.active) {
    const disableText = intl.formatMessage({
      id: "settings.activityTypes.disable",
      defaultMessage: "Disable",
      description: "Disable button title for Activity Type",
    });

    if (activityType.name === DefaultActivityTypeName.EMAIL && organizationSupportEmailService) {
      return (
        <Tooltip
          placement="right"
          title={intl.formatMessage({
            id: "settings.activityTypes.disableEmail",
            defaultMessage:
              "Because your org uses MMC Emails, the Email activity type cannot be disabled. Please contact support or reach out to your account manager to disable emails",
            description: "Disable button is disabled for email activities",
          })}
        >
          <Button className={styles.typeAction} disabled type="default">
            {disableText}
          </Button>
        </Tooltip>
      );
    }
    return (
      <Popconfirm
        cancelText={intl.formatMessage({
          id: "settings.activityTypes.confirmDisable.cancel",
          defaultMessage: "Wait",
          description: "Cancel text of activity type Disable confirmation",
        })}
        description={
          <div>
            <div>
              {intl.formatMessage({
                id: "settings.activityTypes.confirmDisable.paragraph1",
                defaultMessage:
                  "Disabled types will be hidden from dropdowns upon creation of an activity. However, some activities may display this type if created previously or is synced in from an integration.",
                description: "Title of activity type delete confirmation",
              })}
            </div>
            <div className={styles.disableStrongText}>
              {intl.formatMessage({
                id: "settings.activityTypes.confirmDisable.paragraph2",
                defaultMessage: "Are you sure you want to disable this type?",
                description: "Title of activity type delete confirmation",
              })}
            </div>
          </div>
        }
        okText={intl.formatMessage({
          id: "settings.activityTypes.confirmDisable.ok",
          defaultMessage: "Disable type",
          description: "Ok text of activity type Disable confirmation",
        })}
        onConfirm={handleActivate}
      >
        <Button className={styles.typeAction} type="default">
          {disableText}
        </Button>
      </Popconfirm>
    );
  }

  return (
    <Button className={styles.typeAction} onClick={handleActivate} type="default">
      <FormattedMessage
        id="settings.activityTypes.reEnable"
        defaultMessage="Re-Enable"
        description="Re-Enable button title for Activity Type"
      />
    </Button>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypeDeletingIds: getActivityTypeDeletingIds(state),
  organizationSupportEmailService: doesOrganizationSupportEmailService(state),
});

const mapDispatchToProps = {
  onDeleteActivityType: deleteActivityType.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DeleteButton);
